import React, { useMemo } from 'react';
import { STUDIO_SIGNUP_URL } from '../constants';
import { useCalendlyDialog } from './CalendlyDialog';
import { trackLandingEvent } from '../lib/analytics';
import { appendAttributionToUrl } from '../lib/attribution';

function pagePath(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '/';
}

interface TalkToFoundersButtonProps {
  variant?: 'primary' | 'ghost' | 'compact';
  className?: string;
  children?: React.ReactNode;
  analyticsLocation?: string;
}
export const TalkToFoundersButton = React.forwardRef<HTMLButtonElement, TalkToFoundersButtonProps>(
  (
    {
      variant = 'primary',
      className,
      children = 'Talk to Sales',
      analyticsLocation = 'cta',
    },
    ref,
  ) => {
    const { open } = useCalendlyDialog();
    const cls =
      variant === 'compact'
        ? 'btn btn-primary !px-3 !py-2 !text-[10px] sm:!px-5 sm:!py-[13px] sm:!text-[12px]'
        : variant === 'ghost'
          ? 'btn btn-ghost'
          : 'btn btn-primary';
    return (
      <button
        ref={ref}
        type="button"
        onClick={() => {
          trackLandingEvent('generate_lead', {
            method: 'talk_to_sales',
            location: analyticsLocation,
            page_path: pagePath(),
          });
          open();
        }}
        className={className ? `${cls} ${className}` : cls}
      >
        {children}
        <span className="arrow">→</span>
      </button>
    );
  },
);
TalkToFoundersButton.displayName = 'TalkToFoundersButton';

interface SignUpButtonProps {
  variant?: 'primary' | 'ghost' | 'compact';
  className?: string;
  children?: React.ReactNode;
  analyticsLocation?: string;
}
export const SignUpButton = React.forwardRef<HTMLAnchorElement, SignUpButtonProps>(
  (
    {
      variant = 'primary',
      className,
      children = 'Sign Up',
      analyticsLocation = 'cta',
    },
    ref,
  ) => {
    const cls =
      variant === 'compact'
        ? 'btn btn-primary !px-3 !py-2 !text-[10px] sm:!px-5 sm:!py-[13px] sm:!text-[12px]'
        : variant === 'ghost'
          ? 'btn btn-ghost'
          : 'btn btn-primary';
    const href = useMemo(() => appendAttributionToUrl(STUDIO_SIGNUP_URL), []);
    return (
      <a
        ref={ref}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className ? `${cls} ${className}` : cls}
        onClick={() => {
          trackLandingEvent('cta_signup_click', {
            location: analyticsLocation,
            page_path: pagePath(),
          });
        }}
      >
        {children}
        <span className="arrow">→</span>
      </a>
    );
  },
);
SignUpButton.displayName = 'SignUpButton';

/** @deprecated Use SignUpButton instead. Kept for WaitListModal compatibility. */
interface JoinWaitlistButtonProps {
  onClick: () => void;
  variant?: 'button' | 'link' | 'primary';
  className?: string;
  children?: React.ReactNode;
}

export function JoinWaitlistButton({
  onClick,
  variant = 'button',
  className,
  children = 'Sign Up',
}: JoinWaitlistButtonProps) {
  if (variant === 'link') {
    return (
      <button type="button" onClick={onClick} className={className ? `link-tertiary ${className}` : 'link-tertiary'}>
        {children}
      </button>
    );
  }
  if (variant === 'primary') {
    const cls = className ? `btn btn-primary ${className}` : 'btn btn-primary';
    return (
      <button type="button" onClick={onClick} className={cls}>
        {children}
        <span className="arrow">→</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border border-[rgba(255,255,255,0.25)] bg-[rgba(255,255,255,0.08)] backdrop-blur-sm px-5 py-2 rounded${className ? ` ${className}` : ''}`}
    >
      {children}
    </button>
  );
}
